import {
  MarketAccessory,
  MarketButton,
  MarketButtonDropdown,
  MarketInputText,
  MarketList,
  MarketRow,
} from '@market/react'; // eslint-disable-line square/no-legacy-market-imports
import { type RefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketMagnifyingGlassIcon } from '@squareup/market-react/icons';
import { EmptyState } from './EmptyState/EmptyState';
import {
  LocationRow,
  type LocationRowState,
} from './LocationRow/LocationRow';
import styles from './LocationSelector.module.css';

const MIN_LOCATIONS_FOR_SEARCH = 5;

export interface MarketButtonDropdownProps {
  popoverPlacement?: 'bottom' | 'bottom-start' | 'bottom-end';
}

export interface Location {
  rowState?: LocationRowState;
  locationId: string;
  locationLabel: string;
  selectLocation: () => void;
}

export interface LocationSelectorProps {
  readonly selectedLocationLabel: string;
  readonly selectedLocationId: string;
  readonly searchInputValue: string;
  readonly setSearchInputValue: (value: string) => void;
  readonly locations: Location[];
  readonly dropdownRef: RefObject<HTMLMarketButtonDropdownElement | null>;
  readonly rank?: 'primary' | 'secondary' | 'tertiary';
  readonly dropdownProps?: MarketButtonDropdownProps;
  readonly onOpen?: () => void;
}

export const LocationSelector = ({
  selectedLocationLabel,
  locations,
  selectedLocationId,
  onOpen,
  setSearchInputValue,
  searchInputValue,
  dropdownRef,
  rank = 'tertiary',
  dropdownProps,
}: LocationSelectorProps) => {
  const { t } = useTranslation();
  const initialLocationsLength = useRef(locations.length);

  return (
    <MarketButtonDropdown
      ref={dropdownRef}
      persistListSelections
      popoverPlacement="bottom"
      interaction="persistent"
      className={styles.buttonDropdown}
      onMarketButtonDropdownOpened={onOpen}
      {...dropdownProps}
    >
      <MarketButton
        className={styles.buttonInner}
        slot="trigger"
        rank={rank}
        size="small"
        data-testid="location-selector-button"
      >
        {selectedLocationLabel}
      </MarketButton>

      <div slot="content" className={styles.menu}>
        {initialLocationsLength.current >= MIN_LOCATIONS_FOR_SEARCH
          ? (
            <MarketRow className={styles.searchRow}>
              <MarketInputText
                value={searchInputValue}
                data-testid="location-selector-input"
                onMarketInputValueChange={({ detail: { value } }) => setSearchInputValue(value)}
              >
                <label>{t('bankingShared.locationSelector.search')}</label>
                <MarketAccessory
                  slot="leading-accessory"
                  size="icon"
                  className={styles.searchIcon}
                >
                  <MarketMagnifyingGlassIcon />
                </MarketAccessory>
              </MarketInputText>
            </MarketRow>
          )
          : null}

        {locations.length > 0
          ? (
            <MarketList value={selectedLocationId}>
              {locations.map((location) => (
                <LocationRow
                  key={location.locationId}
                  locationId={location.locationId}
                  locationLabel={location.locationLabel}
                  selectLocation={location.selectLocation}
                  state={location.rowState}
                />
              ))}
            </MarketList>
          )
          : (
            <EmptyState />
          )}
      </div>
    </MarketButtonDropdown>
  );
};
